import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import SubscriptionTemplate from './SubscriptionTemplate';
import { displayMessage, logSocketError } from './wsHelpers';
import {
	updateLibrary,
	deleteLibrary,
	uploadLibraryMedia,
	addChildLibrary,
	updateChildLibrary,
	deleteChildLibrary,
	addGroupsToLibrary,
	removeGroupsFromLibrary,
} from '@/redux/actions';

const WS_LIBRARY_BY_HTTP = {
	UPLOAD_MEDIA: 'media/:eventID/POST',
	UPDATE_LIBRARY: 'library/:libraryID/PATCH',
	DELETE_LIBRARY: 'library/:libraryID/DELETE',
	ADD_CHILD_LIBRARY: 'library/:parentLibraryID/POST',
	UPDATE_CHILD_LIBRARY: 'library/:parentLibraryID/PATCH',
	DELETE_CHILD_LIBRARY: 'library/:parentLibraryID/DELETE',
	ADD_GROUPS: 'library/:libraryID/addGroups/PATCH',
	REMOVE_GROUPS: 'library/:libraryID/removeGroups/PATCH',
};

const LibrarySubscriptions = props => {
	const {
		socket,
		materialLibraryID,
		uploadMedia,
		updateLibrary,
		deleteLibrary,
		addChildLibrary,
		updateChildLibrary,
		deleteChildLibrary,
		addGroups,
		removeGroups,
		children,
	} = props;
	const propsToSend = { ...props };
	const { id } = useParams();
	const libraryID = id || materialLibraryID;
	delete propsToSend.children;

	const subscribeToLibrary = () => {
		socket.emit('library/:libraryID/SUBSCRIBE', { libraryID });
	};

	const unsubscribeToLibrary = () => {
		socket.emit('library/:libraryID/UNSUBSCRIBE', { libraryID });
	};

	const listenForCreatedMedia = () => {
		socket.on(WS_LIBRARY_BY_HTTP.UPLOAD_MEDIA, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error creating media', error);
			else {
				const { name } = data;
				uploadMedia(data);
				displayMessage(`${name} has been uploaded`);
			}
		});
	};

	const listenForUpdatedLibrary = () =>
		socket.on(WS_LIBRARY_BY_HTTP.UPDATE_LIBRARY, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Library could not be updated', error);
			else {
				const { name } = data;
				updateLibrary(data);
				displayMessage(`${name} library updated`);
			}
		});

	const listenForDeletedLibrary = () =>
		socket.on(WS_LIBRARY_BY_HTTP.DELETE_LIBRARY, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error while deleting library', error);
			else {
				const { libraryName } = data;
				deleteLibrary(data);
				displayMessage(`${libraryName} Library deleted`);
				window.history.back();
			}
		});

	//child library added
	const listenForAddedChildLibrary = () => {
		socket.on(WS_LIBRARY_BY_HTTP.ADD_CHILD_LIBRARY, ({ data }) => {
			addChildLibrary(data);
			displayMessage(`${data.name} library added`);
		});
	};

	//library child updated
	const listenForUpdatedChildLibrary = () => {
		socket.on(WS_LIBRARY_BY_HTTP.UPDATE_CHILD_LIBRARY, ({ data }) => {
			updateChildLibrary(data);
			displayMessage(`${data.name} library updated`);
		});
	};

	//child library deleted
	const listenForDeletedChildLibrary = () => {
		socket.on(WS_LIBRARY_BY_HTTP.DELETE_CHILD_LIBRARY, ({ data }) => {
			const { id, libraryName } = data;
			deleteChildLibrary(id);
			displayMessage(`${libraryName} library delete`);
		});
	};

	//group added to library
	const listenForAddedGroups = () => {
		socket.on(WS_LIBRARY_BY_HTTP.ADD_GROUPS, payload => {
			const { data, error } = payload;
			if (error)
				logSocketError(error, {
					location: 'LibrarySubscriptions.listenForAddedGroups',
					message: 'Error adding groups to child library',
				});
			else {
				const { libraryID, groups } = data;
				addGroups(libraryID, groups);
				displayMessage('Groups added');
			}
		});
	};

	//group removed from library
	const listenForRemovedGroups = () => {
		socket.on(WS_LIBRARY_BY_HTTP.REMOVE_GROUPS, payload => {
			const { data, error } = payload;
			if (error)
				logSocketError(error, {
					location: 'LibrarySubscriptions.listenForRemovedGroups',
					message: 'Error removing groups from library',
				});
			else {
				const { libraryID, groupIDs } = data;
				removeGroups(libraryID, groupIDs);
				displayMessage('Groups removed');
			}
		});
	};

	const listeners = () => [
		listenForUpdatedLibrary,
		listenForDeletedLibrary,
		listenForCreatedMedia,
		listenForAddedChildLibrary,
		listenForUpdatedChildLibrary,
		listenForDeletedChildLibrary,
		listenForAddedGroups,
		listenForRemovedGroups,
	];

	return (
		<>
			<SubscriptionTemplate
				{...props}
				sub={subscribeToLibrary}
				unsub={unsubscribeToLibrary}
				listeners={listeners()}
				routes={Object.values(WS_LIBRARY_BY_HTTP)}
				from="LibrarySubscriptions"
			/>
			{React.cloneElement(children, propsToSend)}
		</>
	);
};

const msp = ({ ws, auth, events }) => ({
	socket: ws.socket,
	auth0Token: auth.auth0Token,
	currentEventId: events.currentEvent.id,
});

const mdp = dispatch => ({
	updateLibrary: library => dispatch(updateLibrary(library)),
	deleteLibrary: libraryID => dispatch(deleteLibrary(libraryID)),
	uploadMedia: media => dispatch(uploadLibraryMedia(media)),
	updateChildLibrary: library => dispatch(updateChildLibrary(library)),
	addChildLibrary: library => dispatch(addChildLibrary(library)),
	deleteChildLibrary: libraryID => dispatch(deleteChildLibrary(libraryID)),
	addGroups: (libraryID, groups) => dispatch(addGroupsToLibrary(libraryID, groups)),
	removeGroups: (libraryID, groupIDs) => dispatch(removeGroupsFromLibrary(libraryID, groupIDs)),
});

export default connect(msp, mdp)(LibrarySubscriptions);
